import { CdkTableModule } from '@angular/cdk/table';
import { CommonModule } from '@angular/common';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { MarkdownModule } from 'ngx-markdown';
import { MatDateFnsModule } from '@angular/material-date-fns-adapter';
import { AutocompleteSearchComponent } from './autocomplete-search/autocomplete-search.component';
import { AvatarStackComponent } from './avatar-stack/avatar-stack.component';
import { AvatarComponent } from './avatar/avatar.component';
import { BadgeComponent } from './badge/badge.component';
import { BarLoaderComponent } from './bar-loader/bar-loader.component';
import { CircularLoaderComponent } from './circular-loader/circular-loader.component';
import { ConfirmationDialogComponent } from './confirmation-dialog/confirmation-dialog.component';
import { CustomerSelectionViewComponent } from './customer-selection-view/customer-selection-view.component';
import { ElementLoaderComponent } from './element-loader/element-loader.component';
import { FileCardComponent } from './file-card/file-card.component';
import { FileIconComponent } from './file-icon/file-icon.component';
import { FilePickerComponent } from './file-picker/file-picker.component';
import {
  OrganisationFormComponent,
  OrganisationPreferencesFormComponent,
  TenantFormComponent,
} from './forms/index';
import { GUARDS } from './guards';
import { HasQualificationDirective } from './has-qualification/has-qualification.directive';
import { RoleDirective } from './has-role/has-role.directive';
import { GroupAdminDirective } from './group-admin/group-admin.directive';
import { HeartButtonComponent } from './heart-button/heart-button.component';
import { IconComponent } from './icon/icon.component';
import { ImageLoaderComponent } from './image-loader/image-loader.component';
import { InviteActionsComponent } from './invite-actions/invite-actions.component';
import { InviteComponent } from './invite/invite.component';
import { LinkCardComponent } from './link-card/link-card.component';
import { MaterialModule } from './material.module';
import { OrganisationConfigDirective } from './organisation-config/organisation-config.directive';
import { PhotoPickerItemComponent } from './photo-picker/photo-picker.component';
import { PIPES, RolePipe } from './pipes/index';
import { RatingComponent } from './rating/rating.component';
import { RemoteConfigDirective } from './remote-config/remote-config.directive';
import { RowResultComponent } from './row-result/row-result.component';
import { SELECTORS } from './selectors/index';
import { SentResetComponent } from './sent-reset/sent-reset.component';
import { RestartModalComponent } from './services/app-restart/restart-modal.component';
import { AppRestartService, GoogleGeocodeService } from './services/index';
import { CsvPreviewComponent } from './tables/csv-preview/csv-preview.component';
import {
  DataListCellPlaceholderDirective,
  DataListCellRendererDirective,
  DataListSimpleCellComponent,
  DataListTableComponent,
} from './tables/data-list-table/data-list-table.component';
import { OrganisationsTableComponent } from './tables/index';
import { MemoCategoriesTableComponent } from './tables/memo-categories/memo-categories-table.component';
import { ListTileComponent } from './list-tile/list-tile.component';
import { LottieComponent, provideLottieOptions } from 'ngx-lottie';
import player from 'lottie-web';
import { MemoActionIconComponent } from './memo-action-icon/memo-action-icon.component';
import { SelectRoleDialogComponent } from './select-role-dialog/select-role-dialog.component';
import { IntersectionObserverDirective } from './intersection-observer/intersection-observer.directive';
import { BrandingComponent } from '../organisation/branding/branding.component';
import { ColorPickerDirective } from 'ngx-color-picker';
import { TenantsTableComponent } from './tables/tenants/tenants-table.component';
import { OrganisationListSelectDialogComponent } from './organisation-list-dialog/organisation-list-select-dialog.component';
import { OrganisationFilterPipe } from './organisation-filter-pipe/organisation-filter.pipe';
import { InvolvedInOrgsPipe } from './involved-in-orgs-pipe/involved-in-orgs.pipe';
import { OrganisationSortPipe } from './organisation-sort-pipe/organisation-sort.pipe';
import { MemberImportCsvPreviewComponent } from './tables/bulk-member-import-table/member-import-csv-preview.component';
import { MemberImportComponent } from './member-import/member-import.component';
import { EasterCategoryComponent } from './animated-category/animated-category.component';
import { CustomerFilterComponent } from './customer-filter/customer-filter.component';
import { CustomerSelectDialogComponent } from './customer-select/customer-select-dialog.component';
import { OrganisationPreferenceDirective } from './organisation-preference/organisation-preference.directive';
import { InputConfirmationDialogComponent } from './input-confirmation-dialog/input-confirmation-dialog.component';
import { AutoAnimateDirective } from './auto-animate/auto-animate.directive';
import { DataListImageCellComponent } from './tables/data-list-table/image-cell/data-list-image-cell.component';
import { EntityHistoryDialogComponent } from './entity-history-dialog/entity-history-dialog.component';
import { EntityHistoryItemComponent } from './entity-history-item/entity-history-item.component';
import { ImagePreviewComponent } from './image-preview/image-preview.component';
import { FileHistoryDialogComponent } from './file-history-dialog/file-history-dialog.component';
import { OrganisationListSelectComponent } from './organisation-list/organisation-list-select.component';
import { BadgeWrapperComponent } from './badge-wrapper/badge-wrapper.component';
import { SelectMemberBadgesComponent } from './select-member-badges/select-member-badges.component';
import { CaDesignModule } from '../ca-design/ca-design.module';
import { MemoFormComponent } from './forms/memo/memo-form.component';
import { MemoRelatesToSelectorComponent } from './forms/memo/components/relates-to-selector/memo-relates-to-selector.component';
import { MemberFilterComponent } from './member-filter/member-filter.component';
import { SwiperPaginationComponent } from './swiper-pagination/swiper-pagination.component';
import { MediaUploadErrorOverlayComponent } from './forms/memo/components/media-upload-error-overlay/media-upload-error-overlay.component';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { WINDOW } from '../app.di';
// Note we need a separate function as it's required
// by the AOT compiler.
export function playerFactory() {
  return player;
}

/**
 * Do not specify providers for modules that might be imported by a lazy loaded module.
 */

const COMPONENTS = [
  AutoAnimateDirective,
  AvatarComponent,
  AvatarStackComponent,
  BadgeComponent,
  BadgeWrapperComponent,
  BarLoaderComponent,
  BrandingComponent,
  FileIconComponent,
  CircularLoaderComponent,
  ConfirmationDialogComponent,
  InputConfirmationDialogComponent,
  CsvPreviewComponent,
  CustomerFilterComponent,
  CustomerSelectionViewComponent,
  DataListTableComponent,
  DataListCellRendererDirective,
  DataListCellPlaceholderDirective,
  DataListImageCellComponent,
  DataListSimpleCellComponent,
  ElementLoaderComponent,
  EntityHistoryDialogComponent,
  EntityHistoryItemComponent,
  FileCardComponent,
  FileHistoryDialogComponent,
  FilePickerComponent,
  HasQualificationDirective,
  HeartButtonComponent,
  GroupAdminDirective,
  RoleDirective,
  RemoteConfigDirective,
  IconComponent,
  ImageLoaderComponent,
  ImagePreviewComponent,
  InviteComponent,
  InviteActionsComponent,
  InvolvedInOrgsPipe,
  LinkCardComponent,
  MemoCategoriesTableComponent,
  MemoFormComponent,
  MemoRelatesToSelectorComponent,
  MemberImportCsvPreviewComponent,
  MemberImportComponent,
  MemberFilterComponent,
  OrganisationConfigDirective,
  OrganisationFilterPipe,
  OrganisationFormComponent,
  OrganisationPreferencesFormComponent,
  OrganisationsTableComponent,
  OrganisationListSelectComponent,
  OrganisationListSelectDialogComponent,
  OrganisationPreferenceDirective,
  OrganisationSortPipe,
  PhotoPickerItemComponent,
  RatingComponent,
  RestartModalComponent,
  RowResultComponent,
  SentResetComponent,
  TenantsTableComponent,
  TenantFormComponent,
  AutocompleteSearchComponent,
  ListTileComponent,
  MemoActionIconComponent,
  SelectMemberBadgesComponent,
  SelectRoleDialogComponent,
  SwiperPaginationComponent,
  IntersectionObserverDirective,
  EasterCategoryComponent,
  CustomerSelectDialogComponent,

  ...SELECTORS,
];
@NgModule({
  imports: [
    LottieComponent,
    CaDesignModule,
    CommonModule,
    RouterModule,
    FormsModule,
    ColorPickerDirective,
    MaterialModule,
    MarkdownModule.forChild(),
    MatDateFnsModule,
    ReactiveFormsModule,
    TranslateModule,
    CdkTableModule,
    MediaUploadErrorOverlayComponent,
  ],
  declarations: [...COMPONENTS, ...PIPES],
  exports: [
    ...COMPONENTS,
    ...PIPES,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
    MediaUploadErrorOverlayComponent,
  ],
  providers: [
    provideCharts(withDefaultRegisterables()),
    provideLottieOptions({
      player: () => player,
    }),
    ...GUARDS,
    AppRestartService,
    GoogleGeocodeService,
    AppRestartService,
    RolePipe,
  ],
})
export class SharedModule {
  static forRoot(): ModuleWithProviders<SharedModule> {
    return {
      ngModule: SharedModule,
      providers: [{ provide: WINDOW, useValue: window }],
    };
  }
}
